import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { driver, type AllowedButtons, type Driver } from 'driver.js';

import {
  IWalkthroughRenderer,
  WalkthroughStepRender,
  walkthroughNextText,
  walkthroughProgressText,
  walkthroughShowButtons,
} from '@ups/xplat/base/in-app-help';

/**
 * The web walkthrough renderer — spotlight, backdrop, positioned popover,
 * keyboard/Esc — via driver.js. One persistent driver instance is reused across
 * steps and tours; the player drives it one step at a time (so cross-page tours
 * keep the NgRx state machine as the source of truth rather than driver's
 * internal index).
 *
 * Buttons use driver's native popover with per-step `onNextClick`/`onPrevClick`/
 * `onCloseClick` hooks wired back to the player. Esc / programmatic close route
 * through `onDestroyStarted` to the current step's `onEnd`. Backdrop clicks are
 * intentionally ignored (overlayClickBehavior no-op) so a guided tour isn't
 * dismissed by an accidental page click.
 */
@Injectable({ providedIn: 'root' })
export class WalkthroughDriverService implements IWalkthroughRenderer<HTMLElement> {
  private instance: Driver | null = null;
  /** Current step's end handler, invoked when the user closes via Esc. */
  private onClose: (() => void) | null = null;

  constructor(private readonly sanitizer: DomSanitizer) {}

  /** driver.js scrolls its own target into view, so there is nothing to prepare. */
  prepareAnchor(): Promise<void> {
    return Promise.resolve();
  }

  /** Spotlight an element with its step popover. */
  highlightAnchor(element: HTMLElement, step: WalkthroughStepRender): void {
    this.onClose = step.onEnd;
    this.ensure().highlight({
      element,
      popover: this.buildPopover(step, 'bottom'),
    });
  }

  /**
   * Show a centered (element-less) popover — used for a teaching nav step that
   * describes a page transition and has no element to anchor to. driver.js renders
   * a step with no `element` as a centered modal over the backdrop.
   */
  showCentered(step: WalkthroughStepRender): void {
    this.onClose = step.onEnd;
    this.ensure().highlight({
      popover: this.buildPopover(step, 'over'),
    });
  }

  /** Tear down the current spotlight/popover (tour ended or moved to a nav hop). */
  destroy(): void {
    this.onClose = null;
    if (this.instance?.isActive()) {
      this.instance.destroy();
    }
  }

  /** Whether a driver spotlight is currently on screen. */
  isActive(): boolean {
    return !!this.instance?.isActive();
  }

  private buildPopover(step: WalkthroughStepRender, side: 'bottom' | 'over') {
    return {
      title: step.title,
      description: this.sanitize(step.html),
      // 'over' has no real side; driver centers an element-less step regardless,
      // so this only matters for anchored steps (default below, auto-flips to fit).
      side: side === 'over' ? undefined : ('bottom' as const),
      align: 'start' as const,
      showButtons: walkthroughShowButtons(step) as AllowedButtons[],
      showProgress: step.total > 1,
      progressText: walkthroughProgressText(step.index, step.total),
      nextBtnText: walkthroughNextText(step.isLast),
      prevBtnText: 'Previous',
      onNextClick: () => (step.isLast ? step.onEnd() : step.onNext()),
      onPrevClick: () => step.onPrev(),
      onCloseClick: () => step.onEnd(),
    };
  }

  /** Angular-sanitized author HTML — driver.js assigns description via innerHTML
   * with no sanitization of its own, so this is the XSS guard the old
   * `[innerHTML]` binding provided. */
  private sanitize(html?: string): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, html ?? '') ?? '';
  }

  private ensure(): Driver {
    if (!this.instance) {
      this.instance = driver({
        animate: true,
        allowClose: true, // Esc closes; the X button closes via onCloseClick
        overlayColor: 'rgb(0, 0, 0)',
        overlayOpacity: 0.5,
        stagePadding: 8,
        stageRadius: 4,
        popoverClass: 'ups-walkthrough',
        // Ignore backdrop clicks so a guided tour isn't dismissed by an accidental
        // page click (the old spotlight was click-through / non-dismissing).
        overlayClickBehavior: () => undefined,
        // Esc / other driver-initiated closes route back to the player's end() so
        // NgRx state and the spotlight tear down together. Programmatic destroy()
        // uses driver's internal path (e=false) and does not re-enter this.
        onDestroyStarted: () => this.onClose?.(),
      });
    }
    return this.instance;
  }
}
